import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ApiError,
  decideAccessRequest,
  fetchAccessDecision,
  fetchChecklist,
  fetchList,
  fetchTemplate,
  type ApiAccessDecision,
} from "../lib/api.js";
import { AuthShell, ErrorBanner, SuccessBanner } from "../components/AuthShell.js";
import { Button } from "../components/ui/Button.js";
import { useAuth } from "../context/AuthContext.js";

function kindLabel(kind: ApiAccessDecision["resourceKind"]): string {
  return kind === "checklist" ? "checklist" : kind === "template" ? "template" : "list";
}

function resourcePath(decision: ApiAccessDecision): string {
  const segment =
    decision.resourceKind === "list"
      ? "lists"
      : decision.resourceKind === "template"
        ? "templates"
        : "checklists";
  return `/${segment}/${decision.resourceId}`;
}

export function AccessDecisionPage() {
  const { token } = useParams<{ token: string }>();
  const { user } = useAuth();
  const [decision, setDecision] = useState<ApiAccessDecision | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [busy, setBusy] = useState<"read" | "write" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [complete, setComplete] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("This review link is invalid.");
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const result = await fetchAccessDecision(token);
        if (!cancelled) setDecision(result);
      } catch (err) {
        if (!cancelled) {
          if (err instanceof ApiError && err.status === 410) setExpired(true);
          else setError(err instanceof ApiError ? err.message : "Could not load this request.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!decision || !user) {
      setIsOwner(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const resource =
          decision.resourceKind === "list"
            ? await fetchList(decision.resourceId)
            : decision.resourceKind === "template"
              ? await fetchTemplate(decision.resourceId)
              : await fetchChecklist(decision.resourceId);
        if (!cancelled) setIsOwner(resource.ownerId === user.sub);
      } catch {
        if (!cancelled) setIsOwner(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [decision, user]);

  async function submit(decisionValue: "approve" | "deny", role?: "read" | "write") {
    if (!token) return;
    const busyValue = decisionValue === "deny" ? "deny" : role!;
    setBusy(busyValue);
    setError(null);
    try {
      const result = await decideAccessRequest(token, { decision: decisionValue, ...(role ? { role } : {}) });
      setDecision((current) =>
        current
          ? {
              ...current,
              status: result.status,
              alreadyDecided: true,
            }
          : current
      );
      setComplete(
        decisionValue === "deny"
          ? "Access request declined. We've notified the requester."
          : `Access granted. We've emailed the requester a ${role === "write" ? "can-edit" : "view-only"} link.`
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === "DECISION_ALREADY_USED") {
        setDecision((current) => (current ? { ...current, alreadyDecided: true } : current));
        setComplete("This request was already decided.");
      } else if (err instanceof ApiError && err.status === 410) {
        setExpired(true);
      } else {
        setError(err instanceof ApiError ? err.message : "Could not save your decision.");
      }
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <AuthShell title="Loading access request">
        <p className="text-sm text-muted">Just a moment...</p>
      </AuthShell>
    );
  }

  if (expired) {
    return (
      <AuthShell title="This decision link has expired">
        <p className="text-sm text-muted">
          Access requests expire after a short time. Ask the requester to submit a new request if needed.
        </p>
      </AuthShell>
    );
  }

  if (!decision) {
    return (
      <AuthShell title="This request isn't available">
        <ErrorBanner message={error ?? "This review link is invalid."} />
      </AuthShell>
    );
  }

  const alreadyDecided = decision.alreadyDecided || decision.status !== "pending";
  return (
    <AuthShell title={`Review access request`}>
      <p className="text-sm text-muted">
        {decision.requesterName || decision.requesterEmail} wants access to your {kindLabel(decision.resourceKind)}.
      </p>
      <div className="mt-5 space-y-3 rounded-xl border border-line bg-slate-50 p-4 text-sm">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-sm">Resource</p>
          <p className="mt-1 font-semibold text-ink">{decision.resourceName}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-sm">Requester</p>
          <p className="mt-1 text-ink">{decision.requesterName || "No name provided"}</p>
          <p className="text-muted">{decision.requesterEmail}</p>
        </div>
        {decision.message ? (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-sm">Message</p>
            <p className="mt-1 whitespace-pre-wrap text-ink">{decision.message}</p>
          </div>
        ) : null}
      </div>

      <div className="mt-5 space-y-3">
        <ErrorBanner message={error} />
        <SuccessBanner message={complete} />
        {alreadyDecided ? (
          <p className="text-sm text-muted">
            This request has already been {decision.status}.
          </p>
        ) : (
          <>
            <Button
              fullWidth
              disabled={busy !== null}
              onClick={() => void submit("approve", "read")}
            >
              {busy === "read" ? "Granting access..." : "Give view-only access"}
            </Button>
            <Button
              fullWidth
              variant="secondary"
              disabled={busy !== null}
              onClick={() => void submit("approve", "write")}
            >
              {busy === "write" ? "Granting access..." : "Give edit access"}
            </Button>
            <Button
              fullWidth
              variant="ghost"
              className="text-danger"
              disabled={busy !== null}
              onClick={() => void submit("deny")}
            >
              {busy === "deny" ? "Declining..." : "Decline"}
            </Button>
          </>
        )}
      </div>
      <Link className="mt-5 inline-block text-sm font-medium text-brand-700 hover:underline" to="/">
        Return home
      </Link>
      {isOwner ? (
        <Link
          className="mt-3 block text-sm font-medium text-brand-700 hover:underline"
          to={resourcePath(decision)}
        >
          Manage all sharing for this {kindLabel(decision.resourceKind)}
        </Link>
      ) : null}
    </AuthShell>
  );
}
